import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiX,
} from 'react-icons/fi';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import DataEnrichmentCommercialCompare from './DataEnrichmentCommercialCompare';
import { useToast } from '../../context/ToastContext';
import {
  CommercialFieldValues, EMPTY_FIELDS, mapFenrisResponseToFields, toFieldString,
  fetchClientById, fetchExistingClRatingDetails,
  fetchFenrisHistoryList, fetchFenrisHistoryByTransactionId,
  submitLikeDislike, submitFenrisFeedback,
} from './dataEnrichmentCommercialApi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import '../../styles/QuoteProposal.css';
import './DataEnrichmentCommercial.css';

const PAGE_SIZE = 10;

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Success: { label: 'Success', color: '#34d399' },
  Failed: { label: 'Failed', color: '#f87171' },
  'Multiple Matches Found': { label: 'Multiple Matches Found', color: '#60a5fa' },
};

interface HistoryRow {
  insuredId: string;
  insuredName: string;
  transactionId: string;
  userName: string;
  status: string;
  updatedInAMS: boolean;
  created: string;
  isLike?: boolean;
  isDisLike?: boolean;
  totalCount: number;
}

interface MatchCandidate { name: string; [key: string]: any; }

const formatDate2 = (d: string) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return `${(dt.getMonth() + 1).toString().padStart(2, '0')}/${dt.getDate().toString().padStart(2, '0')}/${dt.getFullYear()} ${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`;
  } catch { return d; }
};

/**
 * The history API sometimes sends updatedInAMS as a human-readable string ("Yes"/"No"/"true")
 * rather than a real boolean — a non-empty string like "No" is truthy in JS, so a naive
 * `row.updatedInAMS ? ... : ...` check would wrongly render "Yes". Normalize once here.
 */
const toUpdatedInAmsBool = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1';
  }
  return !!value;
};

const DataEnrichmentCommercialHistory: React.FC = () => {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview modal */
  const [previewRow, setPreviewRow] = useState<HistoryRow | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewExisting, setPreviewExisting] = useState<CommercialFieldValues>({ ...EMPTY_FIELDS });
  const [previewDescription, setPreviewDescription] = useState('');
  const [previewFenris, setPreviewFenris] = useState<CommercialFieldValues>({ ...EMPTY_FIELDS });
  const [previewMatches, setPreviewMatches] = useState<MatchCandidate[]>([]);

  /* Feedback popup */
  const [feedbackRowId, setFeedbackRowId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor] = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const { showSuccess, showError } = useToast();

  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFenrisHistoryList(pageNum, PAGE_SIZE);
      const raw: any[] = Array.isArray(res.data) ? res.data : [];
      const data: HistoryRow[] = raw.map((r) => ({ ...r, updatedInAMS: toUpdatedInAmsBool(r.updatedInAMS) }));
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      setError(e.message || 'Unknown error while getting fenris history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.insuredName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q));
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  const openPreview = useCallback(async (row: HistoryRow) => {
    setPreviewRow(row);
    setPreviewLoading(true);
    setPreviewMatches([]);
    try {
      const [histRes, profileRes] = await Promise.all([
        fetchFenrisHistoryByTransactionId(row.transactionId),
        fetchClientById(row.insuredId),
      ]);

      const info = profileRes.data?.[0];
      const existingFields: CommercialFieldValues = {
        ...EMPTY_FIELDS,
        name: toFieldString(info?.commercialName),
        addressLine1: toFieldString(info?.addressLine1),
        city: toFieldString(info?.city),
        state: toFieldString(info?.stateNameOrAbbreviation),
        zipCode: toFieldString(info?.zipCode),
        phoneNumber: toFieldString(info?.phone),
      };
      try {
        const clRes = await fetchExistingClRatingDetails(row.insuredId);
        if (clRes.status !== 204) {
          const clInfo = clRes.data || {};
          existingFields.primaryNaicsCode = toFieldString(clInfo.primaryNaicCode);
          existingFields.primarySICCode = toFieldString(clInfo.primarySicCode);
          existingFields.yearEstablished = toFieldString(clInfo.yearEstablished);
          existingFields.estimatedPayroll = toFieldString(clInfo.estimatedPayroll);
          existingFields.employees = toFieldString(clInfo.employee);
          existingFields.revenue = toFieldString(clInfo.revenue);
          setPreviewDescription(toFieldString(clInfo.description));
        }
      } catch { /* no existing CL rating data */ }
      setPreviewExisting(existingFields);

      const histData = histRes.data;
      if (histData?.status === 'Multiple Matches Found') {
        const candidates: MatchCandidate[] = histData.matchCandidates || [];
        setPreviewMatches(candidates);
        setPreviewFenris(mapFenrisResponseToFields(candidates[0]));
      } else {
        setPreviewFenris(mapFenrisResponseToFields(histData));
      }
    } catch {
      showError('Error while getting fenris history data.');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const onSelectPreviewMatch = (index: number) => {
    if (previewMatches[index]) setPreviewFenris(mapFenrisResponseToFields(previewMatches[index]));
  };

  const closePreview = () => {
    setPreviewRow(null);
    fetchData(page);
  };

  /* Feedback */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLike ?? null);
    setFeedbackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null); setFeedbackAnchor(null);
    setFeedbackText(''); setFeedbackRating(null); setFeedbackError('');
  }, []);

  const onThumbs = useCallback((like: boolean) => {
    if (!feedbackRowId) return;
    setFeedbackRating(like);
    setRows((prev) => prev.map((r) => r.transactionId === feedbackRowId ? { ...r, isLike: like, isDisLike: !like } : r));
    submitLikeDislike(feedbackRowId, like, !like)
      .then(() => showSuccess('Feedback submitted successfully.'))
      .catch(() => showError('Unknown error while inserting likedislike.'));
    closeFeedback();
  }, [feedbackRowId, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedbackError('Please provide your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Please provide your feedback within 250 characters'); return; }
    setFeedbackSubmitting(true);
    submitFenrisFeedback(feedbackRowId, feedbackText)
      .then(() => {
        showSuccess('Feedback submitted successfully.');
        setFeedbackSubmitting(false);
        closeFeedback();
      })
      .catch(() => {
        showError('Unknown error while inserting feedback.');
        setFeedbackSubmitting(false);
      });
  }, [feedbackRowId, feedbackText, closeFeedback]);

  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId, closeFeedback]);

  return (
    <>
      <motion.div className="qp-history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <div className="qp-history__header">
          <h3 className="qp-history__title dec-history-title--flush">Data Enrichment Commercial History</h3>
          <div className="dec-history-header-actions">
            <div className="cs-client-search__input-wrap dec-history-search--wide">
              <FiSearch size={13} className="cs-client-search__icon" />
              <input className="cs-client-search__input" type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <motion.button className="csh-refresh-btn" onClick={() => fetchData(page)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state dec-empty-state--error"><FiClock size={28} /><p>{error}</p></div>
          ) : loading ? (
            <div className="qp-history-loader dec-loader--history">
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiSearch size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">Fetching enrichment records<span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span></p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiClock size={28} />
              <p>{search ? 'No records match your search.' : 'No data enrichment lookups yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th">Insured Name</th>
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th">User</th>
                    <th className="qp-table__th dec-cell-center">Status</th>
                    <th className="qp-table__th dec-cell-center">Updated in AMS</th>
                    <th className="qp-table__th dec-cell-center">Preview</th>
                    <th className="qp-table__th dec-cell-center">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.transactionId ?? i} className="qp-table__row">
                      <td className="qp-table__td">{row.insuredName || '—'}</td>
                      <td className="qp-table__td dec-td-nowrap">{formatDate2(row.created)}</td>
                      <td className="qp-table__td">{row.userName || '—'}</td>
                      <td className="qp-table__td dec-cell-center">
                        <span
                          className="qp-status-pill"
                          style={{ color: (STATUS_CONFIG[row.status] || STATUS_CONFIG.Failed).color }}
                        >
                          {(STATUS_CONFIG[row.status] || { label: row.status || '—' }).label}
                        </span>
                      </td>
                      <td className="qp-table__td dec-cell-center">
                        <span className="dec-updated-badge" style={{ background: row.updatedInAMS ? 'rgba(52,211,153,0.12)' : 'rgba(239,68,68,0.1)', color: row.updatedInAMS ? '#34d399' : '#f87171' }}>
                          {row.updatedInAMS ? '✓ Yes' : '✗ No'}
                        </span>
                      </td>
                      <td className="qp-table__td dec-cell-center">
                        {row.status === 'Success' || row.status === 'Multiple Matches Found' ? (
                          <button className="qp-table__action-btn" title="Preview" onClick={() => openPreview(row)}>
                            <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                          </button>
                        ) : (
                          <span className="dec-table-note">Not Available</span>
                        )}
                      </td>
                      <td className="qp-table__td dec-cell-center">
                        <button className="qp-table__action-btn dec-action-btn--centered" title="Feedback" onClick={(e) => openFeedback(row, e)}>
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}</span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button key={p} className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`} onClick={() => setPage(p as number)}>{p}</button>)}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div ref={feedbackRef} className="csfb-popup" style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={4} />
          {feedbackError && <span className="dec-feedback-error">{feedbackError}</span>}
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={() => onThumbs(true)} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={() => onThumbs(false)} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedback}>{feedbackSubmitting ? 'Submitting…' : 'Submit'}</button>
          </div>
        </div>,
        document.body,
      )}

      {previewRow && ReactDOM.createPortal(
        <div
          className="dec-modal-overlay"
          onClick={closePreview}
        >
          <div className="ho-modal dec-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <h3 className="ho-modal__title">Data Enrichment Commercial Details</h3>
              </div>
              <button className="ho-modal__close" onClick={closePreview}><FiX size={16} /></button>
            </div>
            <div className="ho-modal__body">
              {previewLoading ? (
                <div className="dec-modal-loading">
                  <FiRefreshCw size={18} className="ho-spin" />
                </div>
              ) : (
                <DataEnrichmentCommercialCompare
                  insuredId={previewRow.insuredId}
                  description={previewDescription}
                  existing={previewExisting}
                  fenris={previewFenris}
                  matches={previewMatches}
                  onSelectMatch={onSelectPreviewMatch}
                  transactionId={previewRow.transactionId}
                  showFeedback={false}
                  onUpdated={() => fetchData(page)}
                />
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default DataEnrichmentCommercialHistory;
